import dayjs from 'dayjs';

// 포맷은 아래 노션의 날짜 기획문서를 참고
// https://www.notion.so/bhsn/d6ff8e2302af43be941cb5b6d222cab5?pvs=4
export const DATE_FORMAT = Object.freeze({
    ISO: 'YYYY-MM-DD',
    DISPLAY: 'YYYY-MM-DD',
    DISPLAY_FULL: 'YYYY-MM-DD A hh:mm',
    DATE_KO: 'YYYY년 M월 D일',
    DATE_EN: 'MMMM D, YYYY',
    DATE_JA: 'YYYY年M月D日',
    TRANSACTION_KO: 'YYYY년 M월 D일 A hh:mm',
    TRANSACTION_EN: 'MMMM D, YYYY hh:mm A',
    TRANSACTION_JA: 'YYYY年M月D日 Ahh:mm',
    TRANSACTION_FULL_KO: 'YYYY년 M월 D일 A hh:mm:ss',
    TRANSACTION_FULL_EN: 'MMMM D, YYYY hh:mm:ss A',
    TRANSACTION_FULL_JA: 'YYYY年M月D日 Ahh:mm:ss',
});

/**
 * 트랜잭션 포맷 가져오기
 * @param {string} locale - 언어 locale
 * @returns {string} - TRANSACTION 포맷
 */
export function getTransactionFormat(locale: string = 'ko') {
    switch (locale) {
        case 'ko':
            return DATE_FORMAT.TRANSACTION_KO;
        case 'en':
            return DATE_FORMAT.TRANSACTION_EN;
        case 'ja':
            return DATE_FORMAT.TRANSACTION_JA;
        default:
            return DATE_FORMAT.TRANSACTION_KO;
    }
}

/**
 * 날짜(시간 제외) 포맷 가져오기 — locale별로 표기 형태가 달라 직접 상수를 쓰지 말고 이 함수로 가져온다.
 * @param {string} locale - 언어 locale
 * @returns {string} - 날짜 포맷 (ko: YYYY년 M월 D일 / en: MMMM D, YYYY / ja: YYYY年M月D日)
 */
export function getDateFormat(locale: string = 'ko') {
    switch (locale) {
        case 'ko':
            return DATE_FORMAT.DATE_KO;
        case 'en':
            return DATE_FORMAT.DATE_EN;
        case 'ja':
            return DATE_FORMAT.DATE_JA;
        default:
            return DATE_FORMAT.DATE_KO;
    }
}

/**
 * 트랜잭션 FULL 포맷 가져오기
 * @param {string} locale - 언어 locale
 * @returns {string} - TRANSACTION_FULL 포맷
 */
export function getFullTransactionFormat(locale: string = 'ko') {
    switch (locale) {
        case 'ko':
            return DATE_FORMAT.TRANSACTION_FULL_KO;
        case 'en':
            return DATE_FORMAT.TRANSACTION_FULL_EN;
        case 'ja':
            return DATE_FORMAT.TRANSACTION_FULL_JA;
        default:
            return DATE_FORMAT.TRANSACTION_FULL_KO;
    }
}

/**
 * 몇 주 전 까지의 날짜 범위 가져오기
 * 이 util은 options 객체를 필히 넣어줘야.
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @param {number} options.ago - 몇 주 전인지 지정
 * @returns {{from: string, to: string}} - 주 범위의 시작 날짜와 끝 날짜
 */
export const getWeeksRangeFromWeeksAgo = (options: { date?: Date; format?: string; ago: number }): { from: string; to: string } => {
    const now = dayjs(options?.date ?? new Date());

    return {
        from: now
            .subtract(options.ago, 'weeks')
            .add(1, 'day')
            .format(options.format ?? DATE_FORMAT.ISO),
        to: now.format(options.format ?? DATE_FORMAT.ISO),
    };
};

/**
 * 몇 개월 전 까지의 날짜 범위 가져오기
 * 이 util은 options 객체를 필히 넣어줘야.
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @returns {{from: string, to: string}} - 한 년 범위의 시작 날짜와 끝 날짜
 */
export const getMonthsRangeFromMonthsAgo = (options: { date?: Date; format?: string; ago: number }): { from: string; to: string } => {
    const now = dayjs(options?.date ?? new Date());

    return {
        from: now
            .subtract(options.ago, 'months')
            .add(1, 'day')
            .format(options.format ?? DATE_FORMAT.ISO),
        to: now.format(options.format ?? DATE_FORMAT.ISO),
    };
};

/**
 * 몇 개월 이후 날짜 범위 가져오기(미래)
 * 이 util은 options 객체를 필히 넣어줘야.
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @returns {{from: string, to: string}} - 한 년 범위의 시작 날짜와 끝 날짜
 */
export const getMonthsRangeFromMonthsAfter = (options: { date?: Date; format?: string; after: number }): { from: string; to: string } => {
    const now = dayjs(options?.date ?? new Date());

    return {
        from: now.format(options.format ?? DATE_FORMAT.ISO),
        to: now
            .add(options.after, 'months')
            .subtract(1, 'day')
            .format(options.format ?? DATE_FORMAT.ISO),
    };
};

/**
 * 몇 년 전 까지의 날짜 범위 가져오기
 * 이 util은 options 객체를 필히 넣어줘야.
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @returns {{from: string, to: string}} - 한 년 범위의 시작 날짜와 끝 날짜
 */
export const getYearRangeFromYearsAgo = (options: { date?: Date; format?: string; ago: number }): { from: string; to: string } => {
    const now = dayjs(options?.date ?? new Date());

    return {
        from: now
            .subtract(options.ago, 'year')
            .add(1, 'day')
            .format(options.format ?? DATE_FORMAT.ISO),
        to: now.format(options.format ?? DATE_FORMAT.ISO),
    };
};

/**
 * 옵션을 통해 전달 된 date로 부터 1년의 범위를 가져옵니다.
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @returns {{from: string, to: string}} - 한 년 범위의 시작 날짜와 끝 날짜
 */
export const getOneYearRange = (options?: { date?: Date; format?: string }): { from: string; to: string } => {
    return getYearRangeFromYearsAgo({
        ...options,
        ago: 1,
    });
};

/**
 * 옵션을 통해 전달 된 date로 부터 10년의 범위를 가져옵니다.
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @returns {{from: string, to: string}} - 한 년 범위의 시작 날짜와 끝 날짜
 */
export const getTenYearRange = (options?: { date?: Date; format?: string }): { from: string; to: string } => {
    return getYearRangeFromYearsAgo({
        ...options,
        ago: 10,
    });
};

/**
 * 옵션을 통해 전달 된 date로 부터 10년의 범위를 가져옵니다.
 * @param {Date} date - 비교 날짜
 * @param {Object} options - 기능 구성 옵션
 * @param {Date} options.date - 기준 날짜 (기본값은 현재 날짜)
 * @param {string} options.format - 날짜 형식 (기본값은 'YYYY-MM-DD')
 * @returns {string} - 경과를 제일 큰 단위와 함께 표시하는 문자열
 */
export const getElapsedFormat = (targetDate: Date | string, referenceDate?: Date | string): string => {
    const now = dayjs(referenceDate ?? new Date());
    const target = dayjs(targetDate);

    const diff = now.diff(target, 'seconds');

    const yearDiff: number = Math.abs(now.diff(target, 'years'));
    const monthDiff: number = Math.abs(now.diff(target, 'months'));
    const dateDiff: number = Math.abs(now.diff(target, 'days'));

    const hourDiff: number = Math.abs(now.diff(target, 'hours'));
    const minuteDiff: number = Math.abs(now.diff(target, 'minutes'));
    const secondDiff: number = Math.abs(now.diff(target, 'seconds'));

    const suffix = diff < 0 ? '후' : '전';

    if (yearDiff > 0) {
        return `${yearDiff}년 ${suffix}`;
    } else if (monthDiff > 0) {
        return `${monthDiff}개월 ${suffix}`;
    } else if (dateDiff > 0) {
        return `${dateDiff}일 ${suffix}`;
    } else if (hourDiff > 0) {
        return `${hourDiff}시간 ${suffix}`;
    } else if (minuteDiff > 0) {
        return `${minuteDiff}분 ${suffix}`;
    } else {
        return `${secondDiff}초 ${suffix}`;
    }
};

/**
 * D-day 계산
 * @param {Date | string} targetDate - 목표 날짜
 * @param {Date | string} referenceDate - 기준 날짜 (기본값은 현재 날짜)
 * @returns {number | null} - D-day 숫자 (양수: 미래, 음수: 과거, 0: 오늘), 유효하지 않으면 null
 */
export const getDday = (targetDate: Date | string | null | undefined, referenceDate?: Date | string): number | null => {
    if (targetDate === null || targetDate === undefined || targetDate === '') {
        return null;
    }

    const target = dayjs(targetDate);
    if (!target.isValid()) {
        return null;
    }

    const reference = dayjs(referenceDate ?? new Date()).startOf('day');
    return target.startOf('day').diff(reference, 'day');
};
